import React, { useState, FormEvent, useEffect } from 'react';
import { FormGroup, InputGroup, Button, AnchorButton, Checkbox, HTMLTable, Spinner } from "@blueprintjs/core";
import { AxiosInstance } from 'axios';
import { useTranslation } from 'react-i18next';

import { Environment, GlData, GlDataItem, TrialBalanceTotals, TrialBalanceTotalsItem } from './Types'
import * as utils from "./utils";

interface TrialBalanceProps {
    svc: AxiosInstance,
    currentEnv?: Environment,
}

function TrialBalance({ svc, currentEnv = {} as Environment }: TrialBalanceProps) {
    const { t, i18n } = useTranslation();
    const [isLoading, setIsLoading] = useState(false)
    //const [data, setData] = useState([] as GlDataItem[])
    const [data, setData] = useState({} as GlData)
    const [totals, setTotals] = useState([0,0,0,0,0,0])
    const [tbTotals, setTBTotals] = useState({} as TrialBalanceTotals)
    const [hasGeneral, setHasGeneral] = useState(false)

    let date = new Date(), y = date.getFullYear(), m = date.getMonth()
    //const [from, setFrom] = useState(utils.formatDate(new Date(y, m, 1)))
    //const [to, setTo] = useState(utils.formatDate(date)) // new Date(y, m+1, 0)
    const [from, setFrom] = useState(utils.formatDate(new Date(currentEnv.startOfYear)))
    const [to, setTo] = useState(utils.formatDateMin(new Date(), new Date(currentEnv.endOfYear)))
    const [unposted, setUnposted] = useState(true)
    const [general, setGeneral] = useState(false)
    const [zero, setZero] = useState(false)

    useEffect(() => {
        //console.log(totals)
    }, [totals])

    const onSubmit = async (e: FormEvent) => {
        e.preventDefault()
        try {
            setIsLoading(true)
            const res = await svc.post('/gldata',
                {
                    type: "",
                    from: from,
                    to: to,
                    includesUnposted: unposted,
                    includesGeneral: general,
                    includesZero: zero,
                });

            //console.log(res.data.data.data)
            //setData(res.data.data.data)
            setIsLoading(false)
            setData(res.data.data)
            setHasGeneral(res.data.data.includesGeneral)

            let bfDr = 0, bfCr = 0, periodDr = 0, periodCr = 0, cfDr = 0, cfCr = 0, ccyTotals = {} as TrialBalanceTotals
            for (let i = 0; i < res.data.data.data.length; i++) {
                let bal = res.data.data.data[i] as GlDataItem
                if (!bal.isGeneral) {
                    // if (bal.bf > 0) bfDr = utils.roundMoney(bfDr + bal.bf)
                    // if (bal.bf < 0) bfCr = utils.roundMoney(bfCr + bal.bf)
                    // if (bal.period > 0) periodDr = utils.roundMoney(periodDr + bal.period)
                    // if (bal.period < 0) periodCr = utils.roundMoney(periodCr + bal.period)
                    // if (bal.cf > 0) cfDr = utils.roundMoney(cfDr + bal.cf)
                    // if (bal.cf < 0) cfCr = utils.roundMoney(cfCr + bal.cf)
                    
                    Object.entries(bal.fbal).map((fbal, i) => {
                        if (fbal[1].bf > 0) bfDr = utils.roundMoney(bfDr + fbal[1].bf)
                        if (fbal[1].bf < 0) bfCr = utils.roundMoney(bfCr + fbal[1].bf)
                        if (fbal[1].period > 0) periodDr = utils.roundMoney(periodDr + fbal[1].period)
                        if (fbal[1].period < 0) periodCr = utils.roundMoney(periodCr + fbal[1].period)
                        if (fbal[1].cf > 0) cfDr = utils.roundMoney(cfDr + fbal[1].cf)
                        if (fbal[1].cf < 0) cfCr = utils.roundMoney(cfCr + fbal[1].cf)

                        if (!(fbal[0] in ccyTotals)) ccyTotals[fbal[0]] = {bfDr: 0, bfCr: 0, periodDr: 0, periodCr: 0, cfDr: 0, cfCr: 0} as TrialBalanceTotalsItem
                        if (fbal[1].bf > 0) ccyTotals[fbal[0]].bfDr += utils.roundMoney(fbal[1].fbf)
                        if (fbal[1].bf < 0) ccyTotals[fbal[0]].bfCr += utils.roundMoney(fbal[1].fbf)
                        if (fbal[1].period > 0) ccyTotals[fbal[0]].periodDr += utils.roundMoney(fbal[1].fperiod)
                        if (fbal[1].period < 0) ccyTotals[fbal[0]].periodCr += utils.roundMoney(fbal[1].fperiod)
                        if (fbal[1].cf > 0) ccyTotals[fbal[0]].cfDr += utils.roundMoney(fbal[1].fcf)
                        if (fbal[1].cf < 0) ccyTotals[fbal[0]].cfCr += utils.roundMoney(fbal[1].fcf)
                    })

                }
            }
            setTotals([bfDr, bfCr, periodDr, periodCr, cfDr, cfCr])
            setTBTotals(ccyTotals)
        } catch (error) {
            utils.showError(error)
        }
    }

    let pageMaxWidth = currentEnv.multiCurrency ? "2200px" : "1300px"
    let pageTitle = t('trial_balance')
    let list = <div className="Print-Align-C" style={{maxWidth: pageMaxWidth}}>
        {isLoading && <Spinner></Spinner>}
        <div className="Spacing-V-16" />
        <div className="No-Screen">
            <div className="Row" style={{maxWidth: pageMaxWidth}}>
                <div className="Col Align-C" style={{fontSize: "21px", fontWeight: 700}}>{currentEnv.companyName}</div>
            </div>
            <div className="Spacing-V-8" />
            {currentEnv.companyAddress && currentEnv.companyAddress.split('\n').map((line) =>
                <div className="Row" style={{maxWidth: pageMaxWidth}}>
                    <div className="Col Align-C">{line}</div>
                </div>
            )}
        </div>
        <div className="Spacing-V-8" />
        <div className="Row" style={{maxWidth: pageMaxWidth}}>
            <div className="Col Align-C" style={{fontSize: "21px", fontWeight: 700}}>{pageTitle}</div>
        </div>
        <div className="Spacing-V-8" />
        <div className="Row" style={{maxWidth: pageMaxWidth}}>
            <div className="Col Col-H Align-C" style={{fontSize: "14px", fontWeight: 600}}>{t('from')}: {data.thisFrom}<div className="Spacing-H-8"/>{t('to')}: {data.thisTo}</div>
        </div>
        <div className="Spacing-V-16" />
        <HTMLTable striped interactive condensed style={{maxWidth: pageMaxWidth}}>
            <thead>
                {currentEnv.multiCurrency && <tr>
                    <th></th>
                    <th></th>
                    <th colSpan={6}><div className="Align-C">{t('in_base_currency')}</div></th>
                    <th className="THead-Sep"></th>
                    <th colSpan={6}><div className="Align-C">{t('in_foreign_currency')}</div></th>
                    <th></th>
                </tr>}
                <tr>
                    <th className="TCol-ACCode"></th>
                    <th className="TCol-ACName"></th>
                    <th className="TCol-ACBalance" colSpan={2}><div className="Align-R">{t('beginning_bal')}</div></th>
                    <th className="TCol-ACBalance" colSpan={2}><div className="Align-R">{t('this_period')}</div></th>
                    <th className="TCol-ACBalance" colSpan={2}><div className="Align-R">{t('current_bal')}</div></th>
                    {currentEnv.multiCurrency && <>
                    <th className="THead-Sep"></th>
                    <th className="TCol-ACBalance" colSpan={2}><div className="Align-R">{t('beginning_bal')}</div></th>
                    <th className="TCol-ACBalance" colSpan={2}><div className="Align-R">{t('this_period')}</div></th>
                    <th className="TCol-ACBalance" colSpan={2}><div className="Align-R">{t('current_bal')}</div></th>
                    <th className="TCol-Currency"></th>
                    </>}
                </tr>
                <tr>
                    <th className="TCol-ACCode">{t('account')}</th>
                    <th className="TCol-ACName">{t('account_name')}</th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('dr')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('cr')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('dr')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('cr')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('dr')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('cr')}</div></th>
                    {currentEnv.multiCurrency && <>
                    <th className="THead-Sep"></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('dr')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('cr')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('dr')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('cr')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('dr')}</div></th>
                    <th className="TCol-ACBalance"><div className="Align-R">{t('cr')}</div></th>
                    <th className="TCol-Currency">{t('currency')}</th>
                    </>}
                </tr>
            </thead>
            <tbody>
                {data.data && data.data.map((item: GlDataItem) =>
                    <>
                    {/* <tr key={item.acCode}>
                        <td className="TCol-ACCode">{item.acCode}</td>
                        <td className="TCol-ACName">{hasGeneral ? <div className={`Indent-${item.level-1}`}>{item.acName1+item.acName2}</div> : item.acName1+item.acName2}</td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.drValue(item.bf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.crValue(item.bf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.drValue(item.period)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.crValue(item.period)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.drValue(item.cf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.crValue(item.cf)}</div></td>
                    </tr> */}
                    {utils.isEmpty(item.fbal) && <tr>
                        <td className="TCol-ACCode">{item.acCode}</td>
                        <td className="TCol-ACName">{(hasGeneral ? <div className={`Indent-${item.level-1}`}>{item.acName1+item.acName2}</div> : item.acName1+item.acName2)}</td>
                        <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                        {currentEnv.multiCurrency && <>
                        <td className="TCol-Sep"></td>
                        <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                        <td className="TCol-ACBalance"><div className="Align-R"></div></td>
                        <td className="TCol-Currency"></td>
                        </>}
                    </tr>
                    }
                    {Object.entries(item.fbal).map((bal, i) => <tr>
                        <td className="TCol-ACCode">{i === 0 && item.acCode}</td>
                        <td className="TCol-ACName">{i === 0 && (hasGeneral ? <div className={`Indent-${item.level-1}`}>{item.acName1+item.acName2}</div> : item.acName1+item.acName2)}</td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.drValue(bal[1].bf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.crValue(bal[1].bf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.drValue(bal[1].period)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.crValue(bal[1].period)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.drValue(bal[1].cf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.crValue(bal[1].cf)}</div></td>
                        {currentEnv.multiCurrency && <>
                        <td className="TCol-Sep"></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.drValue(bal[1].fbf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.crValue(bal[1].fbf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.drValue(bal[1].fperiod)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.crValue(bal[1].fperiod)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.drValue(bal[1].fcf)}</div></td>
                        <td className="TCol-ACBalance"><div className="Align-R">{utils.crValue(bal[1].fcf)}</div></td>
                        <td className="TCol-Currency">{bal[0] === "" ? currentEnv.baseCurrency : bal[0]}</td>
                        </>}
                    </tr>
                    )}
                    </>
                )}
            {/* </tbody>
            <tfoot> */}
                {/* <tr>
                    <td className="TCol-ACCode"></td>
                    <td className="TCol-ACName"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.drValue(totals[0])}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.crValue(totals[1])}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.drValue(totals[2])}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.crValue(totals[3])}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.drValue(totals[4])}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.crValue(totals[5])}</b></div></td>
                </tr> */}

                {Object.entries(tbTotals).map((ccyTotals, i) =>
                (i === 0 || ccyTotals[1].bfDr != 0 || ccyTotals[1].bfCr != 0 || ccyTotals[1].periodDr != 0 || ccyTotals[1].periodCr != 0 || ccyTotals[1].cfDr != 0 || ccyTotals[1].cfCr != 0) && <tr>
                    <td className="TCol-ACCode"></td>
                    <td className="TCol-ACName"></td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.drValue(totals[0])}</b></div>}</td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.crValue(totals[1])}</b></div>}</td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.drValue(totals[2])}</b></div>}</td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.crValue(totals[3])}</b></div>}</td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.drValue(totals[4])}</b></div>}</td>
                    <td className="TCol-ACBalance">{i === 0 && <div className="Align-R"><b>{utils.crValue(totals[5])}</b></div>}</td>
                    {currentEnv.multiCurrency && <>
                    <td className="TCol-Sep"></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.drValue(ccyTotals[1].bfDr)}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.crValue(ccyTotals[1].bfCr)}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.drValue(ccyTotals[1].periodDr)}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.crValue(ccyTotals[1].periodCr)}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.drValue(ccyTotals[1].cfDr)}</b></div></td>
                    <td className="TCol-ACBalance"><div className="Align-R"><b>{utils.crValue(ccyTotals[1].cfCr)}</b></div></td>
                    <td className="TCol-Currency">{ccyTotals[0] === "" ? data.baseCurrency : ccyTotals[0]}</td>
                    </>}
                </tr>
                )}

            {/* </tfoot> */}
            </tbody>
        </HTMLTable>
    </div>

    return (
        <>
            <form className="No-Print" onSubmit={onSubmit}>
                <div className="Row">
                    <FormGroup className="Col-1-1" label={t('from')} labelFor="from">
                        <InputGroup id="from" value={from} type="date" onChange={utils.handleStringChange(s => setFrom(s))} />
                    </FormGroup>
                    <FormGroup className="Col-2-1" label={t('to')} labelFor="to">
                        <InputGroup id="to" value={to} type="date" onChange={utils.handleStringChange(s => setTo(s))} />
                    </FormGroup>
                    <Button className="Col-3-1 Button-LabeledFormGroup" icon="desktop" type="submit" >{t('preview')}</Button>
                    <AnchorButton className="Col-4-1 Button-LabeledFormGroup" icon="download" target="_blank" href={`${svc.defaults.baseURL}/downloadtb?from=${from}&to=${to}`+ (unposted ? "&unposted" : "")+ (general ? "&general" : "")+ (zero ? "&zero" : "")}>Excel</AnchorButton>
                    {/*<AnchorButton className="Col-4-1 Button-LabeledFormGroup" icon="document" target="_blank" href={`${svc.defaults.baseURL}/downloadtb?from=${from}&to=${to}&inline`+ (unposted ? "&unposted" : "")+ (general ? "&general" : "")+ (zero ? "&zero" : "")}>PDF</AnchorButton>*/}
                    {/*<AnchorButton className="Col-5-1 Button-LabeledFormGroup" icon="document" target="_blank" href={`${svc.defaults.baseURL}/downloadtb?from=${from}&to=${to}`+ (unposted ? "&unposted" : "")+ (general ? "&general" : "")+ (zero ? "&zero" : "")}>Download PDF</AnchorButton>*/}
                </div>
                <div className="Row">
                    {/* Checkbox-LabeledFormGroup */}
                    {/*<Checkbox className="Col-1-1" label={t('includes_unposted')} checked={unposted} onChange={utils.handleBooleanChange(v => setUnposted(v))} />*/}
                    <Checkbox className="Col-1-1" label={t('includes_general')} checked={general} onChange={utils.handleBooleanChange(v => setGeneral(v))} />
                    <Checkbox className="Col-2-1" label={t('includes_zero')} checked={zero} onChange={utils.handleBooleanChange(v => setZero(v))} />
                </div>
            </form>
            {list}
        </>
    );
}

export default TrialBalance